<template>
  <div class="pages-container">
    <el-card class="card">
      <div class="title">【图文页管理】</div>
      <div class="center">
        <!-- 操作栏 -->
        <div class="search-bar">
          <el-button type="success" @click="showAddDialog">新增图文页</el-button>
        </div>
        <!-- 列表 -->
        <el-table :data="pages" style="width: 100%" stripe class="pages-table">
          <el-table-column prop="id" label="ID" width="80"></el-table-column>
          <el-table-column prop="pageName" label="页面名称（导航名）"></el-table-column>
          <el-table-column prop="sortOrder" label="排序" width="100"></el-table-column>
          <el-table-column label="操作" width="160">
            <template slot-scope="scope">
              <el-button size="mini" @click="editPage(scope.row)">编辑</el-button>
              <el-button size="mini" type="danger" @click="confirmDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
    <!-- 新增/编辑弹窗 -->
    <el-dialog
        :title="isEdit ? '编辑图文页' : '新增图文页'"
        :visible.sync="dialogVisible"
        width="70%"
        top="5vh"
        destroy-on-close>
      <el-form :model="pageForm" :rules="rules" ref="pageForm" label-position="top">
        <el-form-item label="页面名称（导航名，不允许重复）" prop="pageName">
          <el-input v-model="pageForm.pageName" placeholder="如：关于我们" maxlength="50" show-word-limit></el-input>
        </el-form-item>
        <el-form-item label="排序（数值越小越靠前）" prop="sortOrder">
          <el-input-number v-model="pageForm.sortOrder" :min="0" :max="9999"></el-input-number>
        </el-form-item>
        <el-form-item label="图文内容" prop="content">
          <editor
              v-model="pageForm.content"
              id="page-content-editor"
              apiKey="no-api-key"
              :init="{
                license_key: 'gpl',
                // 去除tinyMCE的logo
                branding: false,
                // 去除右上角的按钮
                promotion: false,
                language_url: '/tinymce/langs/zh_CN.js',
                // 设置中文语言
                language: 'zh_CN',
                plugins: 'advlist anchor autolink charmap code fullscreen help image insertdatetime link lists media preview searchreplace table visualblocks wordcount',
                toolbar: 'undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image',
                height: 400,
              }"
              :tinymceScriptSrc="tinymceScriptSrc"
          />
          <div class="editor-tips">
            <p>提示：可以添加文字、图片、表格等内容</p>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
    <!-- 删除确认弹窗 -->
    <el-dialog title="确认删除" :visible.sync="deleteDialogVisible" width="30%">
      <span>你确定要删除图文页「{{ pageToDelete ? pageToDelete.pageName : '' }}」吗？</span>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="deletePage">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import { getAllPages, getPageById, addPage, updatePage, deletePageById } from '@/api/index.js';

export default {
  name: 'Pages',
  components: { Editor },
  data() {
    return {
      pages: [],
      dialogVisible: false,
      deleteDialogVisible: false,
      isEdit: false,
      saving: false,
      pageToDelete: null,
      pageForm: {
        id: null,
        pageName: '',
        content: '',
        sortOrder: 0
      },
      rules: {
        pageName: [{ required: true, message: '请输入页面名称', trigger: 'blur' }],
        sortOrder: [{ required: true, message: '请输入排序', trigger: 'blur' }],
        content: [{ required: true, message: '请输入图文内容', trigger: 'change' }]
      },
      tinymceScriptSrc: `${process.env.VUE_APP_TINYMCE_URL}/tinymce/tinymce.min.js`
    };
  },
  mounted() {
    this.fetchPages();
  },
  methods: {
    // 图文页导航列表（不含图文 JSON）
    async fetchPages() {
      try {
        const response = await getAllPages();
        if (response.code === 0) {
          this.pages = response.data || [];
        } else {
          this.$message.error('获取图文页列表失败: ' + response.message);
        }
      } catch (error) {
        this.$message.error('获取图文页列表失败: ' + error.message);
      }
    },
    showAddDialog() {
      this.isEdit = false;
      this.pageForm = { id: null, pageName: '', content: '', sortOrder: 0 };
      this.dialogVisible = true;
    },
    // 列表接口不含图文内容，编辑前按 id 拉取完整详情
    async editPage(row) {
      try {
        const response = await getPageById(row.id);
        if (response.code === 0 && response.data) {
          this.isEdit = true;
          this.pageForm = {
            id: response.data.id,
            pageName: response.data.pageName,
            content: response.data.content || '',
            sortOrder: response.data.sortOrder || 0
          };
          this.dialogVisible = true;
        } else {
          this.$message.error('获取图文页详情失败: ' + response.message);
        }
      } catch (error) {
        this.$message.error('获取图文页详情失败: ' + error.message);
      }
    },
    submitForm() {
      this.$refs.pageForm.validate(async (valid) => {
        if (!valid) {
          this.$message.error('请填写完整的图文页信息');
          return;
        }
        this.saving = true;
        try {
          const response = this.isEdit
              ? await updatePage(this.pageForm)
              : await addPage(this.pageForm);
          if (response.code === 0) {
            // 后端成功 message 固定（如图文页添加成功），直接展示
            this.$message.success(response.message);
            this.dialogVisible = false;
            this.fetchPages();
          } else {
            // 校验失败（如页面名称已存在）直接展示后端 message
            this.$message.error(response.message);
          }
        } catch (error) {
          this.$message.error('保存图文页失败: ' + error.message);
        } finally {
          this.saving = false;
        }
      });
    },
    confirmDelete(row) {
      this.pageToDelete = row;
      this.deleteDialogVisible = true;
    },
    async deletePage() {
      try {
        const response = await deletePageById(this.pageToDelete.id);
        if (response.code === 0) {
          this.$message.success(response.message || '删除成功');
          this.deleteDialogVisible = false;
          this.fetchPages();
        } else {
          this.$message.error('删除图文页失败: ' + response.message);
        }
      } catch (error) {
        this.$message.error('删除图文页失败: ' + error.message);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.pages-container {
  padding: 20px;
}

.card {
  min-height: 500px;
}

.title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 20px;
}

.search-bar {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.pages-table {
  margin-bottom: 20px;
}

.editor-tips {
  p {
    font-size: 12px;
    color: #999;
    margin: 5px 0 0;
  }
}

@media (max-width: 768px) {
  .pages-container {
    padding: 10px;
  }

  .el-table {
    font-size: 12px;
  }

  .el-button--mini {
    padding: 5px 10px;
    font-size: 12px;
  }
}
</style>
